import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Auth } from "./components/Auth";
import { TodayDashboard } from "./components/TodayDashboard";
import { PresetsList } from "./components/PresetsList";
import { ScheduleBuilder } from "./components/ScheduleBuilder";
import { History } from "./components/History";
import { Settings } from "./components/Settings";
import { Navigation } from "./components/Navigation";
import { Badges } from "./components/Badges";
import { OnboardingWizard } from "./components/OnboardingWizard";
import {
  NavigationFlow,
  getBreadcrumbs,
  getNextSteps,
} from "./components/NavigationFlow";
import { getActiveSchedule, getTodayPlan, getUserPresets } from "./lib/api";
import {
  shouldShowOnboarding,
  markOnboardingCompleted,
  markOnboardingSkipped,
} from "./lib/onboarding";

function AppContent() {
  const { user, loading } = useAuth();
  const [currentView, setCurrentView] = useState("today");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [authTimeout, setAuthTimeout] = useState(false);
  const [navigationState, setNavigationState] = useState({
    hasSchedule: false,
    hasPlan: false,
    supplementCount: 0,
    loading: true,
  });

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then(() => console.log("Service Worker registered"))
        .catch((err) =>
          console.error("Service Worker registration failed:", err)
        );
    }
  }, []);

  // Add timeout for auth loading to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setAuthTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, [loading]);

  // Reset timeout when loading changes
  useEffect(() => {
    if (!loading) {
      setAuthTimeout(false);
    }
  }, [loading]);

  useEffect(() => {
    // Check if onboarding should be shown when user logs in
    if (user) {
      checkOnboardingStatus();
      loadNavigationState();
    }
  }, [user]);

  const loadNavigationState = async () => {
    try {
      const [schedule, plan, presets] = await Promise.all([
        getActiveSchedule().catch(() => null),
        getTodayPlan().catch(() => null),
        getUserPresets().catch(() => []),
      ]);

      setNavigationState({
        hasSchedule: !!schedule,
        hasPlan: !!plan,
        supplementCount: presets.length,
        loading: false,
      });
    } catch (error) {
      console.error("Error loading navigation state:", error);
      setNavigationState((prev) => ({ ...prev, loading: false }));
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      const shouldShow = await shouldShowOnboarding();
      setShowOnboarding(shouldShow);
    } catch (error) {
      console.error("Error checking onboarding status:", error);
    }
  };

  const handleOnboardingComplete = async () => {
    try {
      await markOnboardingCompleted();
      setShowOnboarding(false);
    } catch (error) {
      console.error("Error completing onboarding:", error);
    }
  };

  const handleOnboardingSkip = async () => {
    try {
      await markOnboardingSkipped();
      setShowOnboarding(false);
    } catch (error) {
      console.error("Error skipping onboarding:", error);
    }
  };

  const restartOnboarding = () => {
    setShowOnboarding(true);
  };

  const handleNavigate = (page: string) => {
    setCurrentView(page);
    // Refresh navigation state when switching views
    if (user) {
      loadNavigationState();
    }
  };

  const getViewTitle = (view: string) => {
    switch (view) {
      case "today":
        return "Dashboard";
      case "presets":
        return "Supplements";
      case "schedule":
        return "Schedule Builder";
      case "history":
        return "History";
      case "badges":
        return "Achievements";
      case "settings":
        return "Settings";
      default:
        return "Dashboard";
    }
  };

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then(() => console.log("Service Worker registered"))
        .catch((err) =>
          console.error("Service Worker registration failed:", err)
        );
    }
  }, []);

  if (loading && !authTimeout) {
    return (
      <div className="mobile-full-height bg-morning-sky flex items-center justify-center p-4 safe-area-full animate-fade-in">
        <div className="bg-warm-card rounded-2xl warm-shadow-lg p-8 w-full max-w-md text-center border border-warm backdrop-blur-md">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-warm-dark mb-2 animate-slide-in">
              Wake & Take
            </h1>
            <p
              className="text-gray-700 animate-slide-in"
              style={{ animationDelay: "200ms" }}
            >
              Loading your account...
            </p>
          </div>

          <div
            className="flex items-center justify-center animate-scale-in"
            style={{ animationDelay: "400ms" }}
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-warm-primary"></div>
          </div>

          <p
            className="text-gray-600 text-sm mt-4 animate-slide-in"
            style={{ animationDelay: "600ms" }}
          >
            Please wait while we verify your session
          </p>
        </div>
      </div>
    );
  }

  // If we hit a timeout or auth failed, show the auth component
  if (!user || authTimeout) {
    return <Auth />;
  }

  return (
    <div className="mobile-container bg-warm-light safe-area-full native-scroll hide-scrollbar no-select">
      {/* Navigation Flow - shown on all pages except dashboard */}
      {currentView !== "today" && (
        <div className="safe-area-top">
          <div className="max-w-4xl mx-auto pt-4 px-4">
            <NavigationFlow
              breadcrumbs={getBreadcrumbs(currentView)}
              nextSteps={getNextSteps(
                currentView,
                navigationState.hasSchedule,
                navigationState.hasPlan,
                navigationState.supplementCount,
                handleNavigate
              )}
              currentPage={currentView}
              onNavigate={handleNavigate}
            />
          </div>
        </div>
      )}

      <div className="mobile-content native-scroll hide-scrollbar">
        {currentView === "today" && <TodayDashboard />}
        {currentView === "presets" && <PresetsList />}
        {currentView === "schedule" && <ScheduleBuilder />}
        {currentView === "history" && <History />}
        {currentView === "badges" && <Badges />}
        {currentView === "settings" && (
          <Settings onRestartOnboarding={restartOnboarding} />
        )}
      </div>

      <Navigation currentView={currentView} onNavigate={handleNavigate} />

      <OnboardingWizard
        isVisible={showOnboarding}
        onComplete={handleOnboardingComplete}
        onSkip={handleOnboardingSkip}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
